import React, { useState, useEffect } from 'react';
import { adminService } from '../services/authService';

interface AuditLog {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  targetUserId: string | null;
  entityType: string;
  entityId: string | null;
  action: string;
  severity: string;
  before: any;
  after: any;
  changes: any;
  requestId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: any;
  createdAt: string;
  actor: {
    id: string;
    name: string;
    email: string;
    role: string;
  } | null;
  target: {
    id: string;
    name: string;
    email: string;
  } | null;
}

interface AuditLogsResponse {
  logs: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    severity: '',
    entityType: '',
    actorEmail: '',
  });

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const data: AuditLogsResponse = await adminService.getAuditLogs({
        page: pagination.page,
        limit: pagination.limit,
        action: filters.action || undefined,
        severity: filters.severity || undefined,
        entityType: filters.entityType || undefined,
      });
      setLogs(data.logs);
      setPagination(data.pagination);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      ADMIN_LOGIN_SUCCESS: 'Başarılı Giriş',
      ADMIN_LOGIN_FAIL: 'Başarısız Giriş',
      ADMIN_LOGOUT: 'Çıkış',
      ADMIN_ADD_CREDITS: 'Kredi Ekleme',
      ADMIN_REMOVE_CREDITS: 'Kredi Çıkarma',
      ADMIN_UPDATE_SUBSCRIPTION: 'Abonelik Güncelleme',
      ADMIN_UPDATE_ROLE: 'Rol Değişikliği',
      ADMIN_CHANGE_PASSWORD: 'Şifre Değişikliği',
    };
    return labels[action] || action;
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      action: '',
      severity: '',
      entityType: '',
      actorEmail: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="mt-1 text-sm text-gray-500">
          Admin işlemlerinin güvenlik kayıtlarını görüntüleyin
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              İşlem Türü
            </label>
            <select
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">Tümü</option>
              <option value="ADMIN_LOGIN_SUCCESS">Başarılı Giriş</option>
              <option value="ADMIN_LOGIN_FAIL">Başarısız Giriş</option>
              <option value="ADMIN_LOGOUT">Çıkış</option>
              <option value="ADMIN_ADD_CREDITS">Kredi Ekleme</option>
              <option value="ADMIN_UPDATE_SUBSCRIPTION">Abonelik Güncelleme</option>
              <option value="ADMIN_UPDATE_ROLE">Rol Değişikliği</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Önem Seviyesi
            </label>
            <select
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="">Tümü</option>
              <option value="LOW">Düşük</option>
              <option value="MEDIUM">Orta</option>
              <option value="HIGH">Yüksek</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Varlık Türü
            </label>
            <select
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={filters.entityType}
              onChange={(e) => handleFilterChange('entityType', e.target.value)}
            >
              <option value="">Tümü</option>
              <option value="Admin">Admin</option>
              <option value="User">Kullanıcı</option>
              <option value="Credits">Krediler</option>
              <option value="Subscription">Abonelik</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Filtreleri Temizle
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Toplam Kayıt</div>
          <div className="text-2xl font-bold text-gray-900">{pagination.total}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Bu Sayfa</div>
          <div className="text-2xl font-bold text-gray-900">{logs.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Sayfa</div>
          <div className="text-2xl font-bold text-gray-900">{pagination.page} / {pagination.pages}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Yüksek Öncelikli</div>
          <div className="text-2xl font-bold text-red-600">
            {logs.filter(l => l.severity === 'HIGH').length}
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih/Saat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Yapan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hedef
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Önem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Detay
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(log.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getActionLabel(log.action)}
                    </div>
                    <div className="text-sm text-gray-500">{log.entityType}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {log.actor ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900">{log.actor.name}</div>
                        <div className="text-sm text-gray-500">{log.actor.email}</div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        {log.actorEmail || 'Bilinmeyen'}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {log.target ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900">{log.target.name}</div>
                        <div className="text-sm text-gray-500">{log.target.email}</div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">-</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(log.severity)}`}>
                      {log.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.ipAddress || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-primary-600 hover:text-primary-900 font-medium"
                    >
                      Görüntüle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {logs.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">Audit log bulunamadı</p>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Önceki
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Toplam <span className="font-medium">{pagination.total}</span> kayıttan{' '}
                  <span className="font-medium">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{' '}
                  -{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  arası gösteriliyor
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Önceki
                  </button>
                  {[...Array(pagination.pages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.page === i + 1
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Sonraki
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Audit Log Detayları
                </h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Kapat</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">İşlem</label>
                    <p className="mt-1 text-sm text-gray-900">{getActionLabel(selectedLog.action)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Önem Seviyesi</label>
                    <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(selectedLog.severity)}`}>
                      {selectedLog.severity}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tarih/Saat</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedLog.createdAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">IP Adresi</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedLog.ipAddress || '-'}</p>
                  </div>
                </div>

                {/* Actor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">İşlemi Yapan</label>
                  {selectedLog.actor ? (
                    <div className="mt-1 text-sm">
                      <p className="text-gray-900">{selectedLog.actor.name} ({selectedLog.actor.role})</p>
                      <p className="text-gray-500">{selectedLog.actor.email}</p>
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">{selectedLog.actorEmail || 'Bilinmeyen'}</p>
                  )}
                </div>

                {/* Target */}
                {selectedLog.target && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Hedef Kullanıcı</label>
                    <div className="mt-1 text-sm">
                      <p className="text-gray-900">{selectedLog.target.name}</p>
                      <p className="text-gray-500">{selectedLog.target.email}</p>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ek Bilgiler</label>
                    <div className="bg-gray-50 rounded-md p-3">
                      <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                        {JSON.stringify(selectedLog.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Before/After */}
                {(selectedLog.before || selectedLog.after) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedLog.before && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Önceki Durum</label>
                        <div className="bg-red-50 rounded-md p-3">
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                            {JSON.stringify(selectedLog.before, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                    {selectedLog.after && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sonraki Durum</label>
                        <div className="bg-green-50 rounded-md p-3">
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                            {JSON.stringify(selectedLog.after, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Technical Details */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Teknik Detaylar</label>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Log ID:</span>
                      <span className="ml-2 text-gray-900 font-mono">{selectedLog.id}</span>
                    </div>
                    {selectedLog.requestId && (
                      <div>
                        <span className="text-gray-500">Request ID:</span>
                        <span className="ml-2 text-gray-900 font-mono">{selectedLog.requestId}</span>
                      </div>
                    )}
                    {selectedLog.userAgent && (
                      <div className="col-span-2">
                        <span className="text-gray-500">User Agent:</span>
                        <span className="ml-2 text-gray-900">{selectedLog.userAgent}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
